import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Star, Plus, Search, Phone, Globe, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/constants';
import BottomNav from '../components/BottomNav';

const LOUNGE_PLACEHOLDER = "https://images.unsplash.com/photo-1574267432553-4b4628081c31?w=400&h=300&fit=crop";

const LoungesPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [lounges, setLounges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [listMode, setListMode] = useState('top25'); // 'top25' or 'new'
  const [locationFilter, setLocationFilter] = useState('us'); // 'us' or 'international'
  const [showAddModal, setShowAddModal] = useState(false);

  const fetchLounges = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        limit: 25,
        sort: listMode === 'new' ? 'newest' : 'rating',
        location_filter: locationFilter
      };
      if (search) params.search = search;
      
      const res = await axios.get(`${API}/lounges`, { params });
      setLounges(res.data);
    } catch (error) {
      console.error('Failed to fetch lounges:', error);
      toast.error('Failed to load lounges');
    } finally {
      setLoading(false);
    }
  }, [listMode, search, locationFilter]);

  useEffect(() => {
    fetchLounges();
  }, [fetchLounges]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLounges();
  };

  return (
    <div className="min-h-screen bg-[#0B0B0C] pb-24">
      {/* Header */}
      <div className="sticky top-0 bg-[#0B0B0C]/95 backdrop-blur-sm z-10 px-4 py-4 border-b border-white/10">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold text-white">Cigar Lounges</h1>
          <Button
            data-testid="add-lounge-button"
            onClick={() => {
              if (!token) {
                navigate('/auth?reason=lounges');
              } else {
                setShowAddModal(true);
              }
            }}
            size="sm"
            className="bg-amber-600 hover:bg-amber-700"
          >
            <Plus size={18} className="mr-1" /> Add
          </Button>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" size={18} />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search lounges..."
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30"
            />
          </div>
        </form>

        {/* Toggle */}
        <div className="flex items-center gap-2">
          <button
            data-testid="top25-lounges-button"
            onClick={() => setListMode('top25')}
            className={`text-base font-bold transition-colors ${
              listMode === 'top25' ? 'text-white' : 'text-white/40 hover:text-white/70'
            }`}
          >
            Top 25
          </button>
          <span className="text-white/30">|</span>
          <button
            data-testid="new-lounges-button"
            onClick={() => setListMode('new')}
            className={`text-base font-bold transition-colors ${
              listMode === 'new' ? 'text-white' : 'text-white/40 hover:text-white/70'
            }`}
          >
            New
          </button>
          <span className="text-white/30 mx-2">•</span>
          <button
            data-testid="us-lounges-button"
            onClick={() => setLocationFilter('us')}
            className={`text-sm font-medium px-2 py-0.5 rounded transition-colors ${
              locationFilter === 'us' 
                ? 'bg-amber-600 text-white' 
                : 'bg-white/10 text-white/60 hover:text-white hover:bg-white/20'
            }`}
          >
            US
          </button>
          <button
            data-testid="international-lounges-button"
            onClick={() => setLocationFilter('international')}
            className={`text-sm font-medium px-2 py-0.5 rounded transition-colors ${
              locationFilter === 'international' 
                ? 'bg-amber-600 text-white' 
                : 'bg-white/10 text-white/60 hover:text-white hover:bg-white/20'
            }`}
          >
            International
          </button>
        </div>
      </div>

      {/* Lounges List */}
      <div className="p-4 space-y-3">
        {loading ? (
          <div className="text-center text-white/50 py-8">Loading...</div>
        ) : lounges.length === 0 ? (
          <div className="text-center py-12">
            <MapPin size={48} className="text-white/20 mx-auto mb-4" />
            <p className="text-white/50 mb-2">No lounges found</p>
            <p className="text-white/30 text-sm">Be the first to add a lounge!</p>
          </div>
        ) : (
          lounges.map((lounge) => (
            <div
              key={lounge.id}
              onClick={() => navigate(`/lounge/${lounge.id}`)}
              className="bg-white/5 border border-white/10 rounded-xl p-3 flex gap-3 cursor-pointer hover:bg-white/10 transition-colors"
            >
              <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-white/10">
                <img
                  src={lounge.image_url || LOUNGE_PLACEHOLDER}
                  alt={lounge.name}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = LOUNGE_PLACEHOLDER; }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-medium truncate">{lounge.name}</h3>
                <p className="text-white/50 text-sm flex items-center gap-1">
                  <MapPin size={12} />
                  {lounge.city}{lounge.state ? `, ${lounge.state}` : ''}{lounge.country && lounge.country !== 'US' ? `, ${lounge.country}` : ''}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex items-center gap-1">
                    <Star className="text-amber-500 fill-amber-500" size={14} />
                    <span className="text-white text-sm font-medium">
                      {lounge.average_rating?.toFixed(1) || '0.0'}
                    </span>
                  </div>
                  <span className="text-white/30 text-xs">
                    ({lounge.rating_count || 0} {lounge.rating_count === 1 ? 'rating' : 'ratings'})
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Lounge Modal */}
      {showAddModal && (
        <AddLoungeModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchLounges();
          }}
          token={token}
        />
      )}

      <BottomNav />
    </div>
  );
};

// Add Lounge Modal Component
const AddLoungeModal = ({ onClose, onSuccess, token }) => {
  const [step, setStep] = useState(1); // 1: name/city, 2: verify/fill, 3: confirm
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    country: 'US',
    phone: '',
    website: '',
    hours: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [isInternational, setIsInternational] = useState(false);

  const US_STATES = [
    { abbr: 'AL', name: 'Alabama' }, { abbr: 'AK', name: 'Alaska' }, { abbr: 'AZ', name: 'Arizona' },
    { abbr: 'AR', name: 'Arkansas' }, { abbr: 'CA', name: 'California' }, { abbr: 'CO', name: 'Colorado' },
    { abbr: 'CT', name: 'Connecticut' }, { abbr: 'DE', name: 'Delaware' }, { abbr: 'FL', name: 'Florida' },
    { abbr: 'GA', name: 'Georgia' }, { abbr: 'HI', name: 'Hawaii' }, { abbr: 'ID', name: 'Idaho' },
    { abbr: 'IL', name: 'Illinois' }, { abbr: 'IN', name: 'Indiana' }, { abbr: 'IA', name: 'Iowa' },
    { abbr: 'KS', name: 'Kansas' }, { abbr: 'KY', name: 'Kentucky' }, { abbr: 'LA', name: 'Louisiana' },
    { abbr: 'ME', name: 'Maine' }, { abbr: 'MD', name: 'Maryland' }, { abbr: 'MA', name: 'Massachusetts' },
    { abbr: 'MI', name: 'Michigan' }, { abbr: 'MN', name: 'Minnesota' }, { abbr: 'MS', name: 'Mississippi' },
    { abbr: 'MO', name: 'Missouri' }, { abbr: 'MT', name: 'Montana' }, { abbr: 'NE', name: 'Nebraska' },
    { abbr: 'NV', name: 'Nevada' }, { abbr: 'NH', name: 'New Hampshire' }, { abbr: 'NJ', name: 'New Jersey' },
    { abbr: 'NM', name: 'New Mexico' }, { abbr: 'NY', name: 'New York' }, { abbr: 'NC', name: 'North Carolina' },
    { abbr: 'ND', name: 'North Dakota' }, { abbr: 'OH', name: 'Ohio' }, { abbr: 'OK', name: 'Oklahoma' },
    { abbr: 'OR', name: 'Oregon' }, { abbr: 'PA', name: 'Pennsylvania' }, { abbr: 'RI', name: 'Rhode Island' },
    { abbr: 'SC', name: 'South Carolina' }, { abbr: 'SD', name: 'South Dakota' }, { abbr: 'TN', name: 'Tennessee' },
    { abbr: 'TX', name: 'Texas' }, { abbr: 'UT', name: 'Utah' }, { abbr: 'VT', name: 'Vermont' },
    { abbr: 'VA', name: 'Virginia' }, { abbr: 'WA', name: 'Washington' }, { abbr: 'WV', name: 'West Virginia' },
    { abbr: 'WI', name: 'Wisconsin' }, { abbr: 'WY', name: 'Wyoming' }, { abbr: 'DC', name: 'Washington DC' }
  ];

  const handleToggleInternational = (intl) => {
    setIsInternational(intl);
    if (intl) {
      setFormData({ ...formData, country: '', state: '' });
    } else {
      setFormData({ ...formData, country: 'US', state: '' });
    }
  };

  const handleVerify = async () => {
    if (!formData.name || !formData.city) {
      toast.error('Please enter lounge name and city');
      return;
    }

    if (isInternational && !formData.country) {
      toast.error('Please enter a country');
      return;
    }

    setVerifying(true);
    try {
      const res = await axios.post(`${API}/lounges/verify`, {
        name: formData.name,
        city: formData.city,
        country: isInternational ? formData.country : 'US'
      });
      
      setVerificationResult(res.data);
      
      if (res.data.verified && res.data.suggested_data) {
        // Auto-fill with suggested data
        setFormData(prev => ({
          ...prev,
          ...res.data.suggested_data,
          // Keep user's original name if they prefer
          name: res.data.suggested_data.name || prev.name
        }));
        toast.success('Lounge verified! Details auto-filled.');
      } else if (!res.data.verified) {
        toast.warning(res.data.message || 'Could not verify this lounge');
      }
      
      setStep(2);
    } catch (error) {
      console.error('Verification error:', error);
      toast.error('Verification failed. You can still add manually.');
      setStep(2);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.city) {
      toast.error('Please fill in name and city');
      return;
    }

    if (!isInternational && !formData.state) {
      toast.error('Please select a state');
      return;
    }

    if (isInternational && !formData.country) {
      toast.error('Please enter a country');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/lounges`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Lounge added successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add lounge');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-[#141416] w-full max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#141416] flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">
            {step === 1 ? 'Add Lounge' : 'Confirm Details'}
          </h2>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            ✕
          </button>
        </div>

        {step === 1 ? (
          /* Step 1: Name and City for verification */
          <div className="p-4 space-y-4">
            {/* US / International Toggle */}
            <div className="flex gap-2 mb-2">
              <button
                type="button"
                onClick={() => handleToggleInternational(false)}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  !isInternational 
                    ? 'bg-amber-600 text-white' 
                    : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
              >
                US Lounge
              </button>
              <button
                type="button"
                onClick={() => handleToggleInternational(true)}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  isInternational 
                    ? 'bg-amber-600 text-white' 
                    : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
              >
                International
              </button>
            </div>

            <div>
              <label className="text-white/70 text-sm mb-1 block">Lounge Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Casa de Montecristo"
                className="bg-white/5 border-white/10 text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-white/70 text-sm mb-1 block">City *</label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="e.g., Chicago"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              {isInternational ? (
                <div>
                  <label className="text-white/70 text-sm mb-1 block">Country *</label>
                  <Input
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    placeholder="e.g., Canada"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              ) : (
                <div>
                  <label className="text-white/70 text-sm mb-1 block">State *</label>
                  <select
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="w-full h-10 px-3 bg-white/5 border border-white/10 text-white rounded-md"
                  >
                    <option value="">Select</option>
                    {US_STATES.map(s => (
                      <option key={s.abbr} value={s.abbr}>{s.abbr} - {s.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm">
              <p className="text-amber-400">
                <Search size={14} className="inline mr-1" />
                We'll try to find this lounge online and auto-fill details. Even if we can't find it, you can still add it manually.
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleVerify}
                disabled={verifying || !formData.name || !formData.city}
                className="flex-1 bg-amber-600 hover:bg-amber-700"
              >
                {verifying ? (
                  <>
                    <Search size={16} className="mr-2 animate-pulse" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <Search size={16} className="mr-2" />
                    Verify & Auto-fill
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(2)}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Skip
              </Button>
            </div>
          </div>
        ) : (
          /* Step 2: Full form with all details */
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {/* Verification Status */}
            {verificationResult && (
              <div className={`rounded-lg p-3 text-sm ${
                verificationResult.verified 
                  ? 'bg-green-500/10 border border-green-500/30' 
                  : 'bg-amber-500/10 border border-amber-500/30'
              }`}>
                <p className={verificationResult.verified ? 'text-green-400' : 'text-amber-400'}>
                  {verificationResult.verified 
                    ? `✓ Verified (${verificationResult.confidence} confidence)` 
                    : `ℹ ${verificationResult.message || 'Verification unavailable'}`}
                </p>
                {!verificationResult.verified && (
                  <p className="text-white/50 text-xs mt-1">
                    You can still add this lounge - just fill in the details below.
                  </p>
                )}
              </div>
            )}

            <div>
              <label className="text-white/70 text-sm mb-1 block">Lounge Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                required
              />
            </div>

            <div>
              <label className="text-white/70 text-sm mb-1 block">Street Address</label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Optional"
                className="bg-white/5 border-white/10 text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-white/70 text-sm mb-1 block">City *</label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  required
                />
              </div>
              {isInternational ? (
                <div>
                  <label className="text-white/70 text-sm mb-1 block">Country *</label>
                  <Input
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                    required
                  />
                </div>
              ) : (
                <div>
                  <label className="text-white/70 text-sm mb-1 block">State *</label>
                  <select
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="w-full h-10 px-3 bg-white/5 border border-white/10 text-white rounded-md"
                    required
                  >
                    <option value="">Select</option>
                    {US_STATES.map(s => (
                      <option key={s.abbr} value={s.abbr}>{s.abbr} - {s.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {isInternational && (
              <div>
                <label className="text-white/70 text-sm mb-1 block">State/Province/Region</label>
                <Input
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  placeholder="Optional"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            )}

            <div>
              <label className="text-white/70 text-sm mb-1 block">Phone</label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="e.g., (312) 555-1234"
                className="bg-white/5 border-white/10 text-white"
              />
            </div>

            <div>
              <label className="text-white/70 text-sm mb-1 block">Website</label>
              <Input
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="e.g., https://example.com"
                className="bg-white/5 border-white/10 text-white"
              />
            </div>

            <div>
              <label className="text-white/70 text-sm mb-1 block">Hours</label>
              <Input
                value={formData.hours}
                onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                placeholder="e.g., Mon-Sat 10am-10pm"
                className="bg-white/5 border-white/10 text-white"
              />
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(1)}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Back
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-amber-600 hover:bg-amber-700"
              >
                {submitting ? 'Adding...' : 'Add Lounge'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default LoungesPage;
